import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { NameAndGuid } from "features/general/NameAndGuidMultiSelectDropdown";
import { Field, GRASS_TYPE, SEEDING_METHOD, useFields } from "model";
import { Dispatch, ReactNode, SetStateAction } from "react";
import GrassTypeDropdown from "../fieldEdit/GrassTypeDropdown";
import EnterDate from "../general/EnterDate";
import SeedingMethodDropdown from "./ReseedMethodDropDown";

const cardSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  p: 2.5,
};

interface ReseedSingleStepProps {
  reseedDate: Date | null;
  setReseedDate: Dispatch<SetStateAction<Date | null>>;
  reseedDateError: boolean;
  setReseedDateError: Dispatch<SetStateAction<boolean>>;
  selectedFields: NameAndGuid[];
  setSelectedFields: Dispatch<SetStateAction<Array<NameAndGuid>>>;
  fieldsSelectInputError: boolean;
  setFieldsSelectInputError: Dispatch<SetStateAction<boolean>>;
  enableFieldSelection: boolean;
  onSeedingMethodChange: (method: SEEDING_METHOD | undefined) => void;
  newGrassType?: string;
  setNewGrassType: Dispatch<SetStateAction<GRASS_TYPE | undefined>>;
  grassTypeError: boolean;
  setGrassTypeError: Dispatch<SetStateAction<boolean>>;
  seedingMethod?: string;
  seedingMethodError: boolean;
  productName: string;
  setProductName: Dispatch<SetStateAction<string>>;
  seedingRate: number;
  setSeedingRate: Dispatch<SetStateAction<number>>;
  notes: string;
  setNotes: Dispatch<SetStateAction<string>>;
  fileUploadSlot?: ReactNode;
}

export function ReseedSingleStep({
  reseedDate,
  setReseedDate,
  reseedDateError,
  selectedFields,
  setSelectedFields,
  fieldsSelectInputError,
  setFieldsSelectInputError,
  enableFieldSelection,
  onSeedingMethodChange,
  newGrassType,
  setNewGrassType,
  grassTypeError,
  setGrassTypeError,
  seedingMethod,
  seedingMethodError,
  productName,
  setProductName,
  seedingRate,
  setSeedingRate,
  notes,
  setNotes,
  fileUploadSlot,
}: ReseedSingleStepProps) {
  const { t } = useTranslation();
  const { data: allFields = [] } = useFields();

  const handleFieldToggle = (field: NameAndGuid) => {
    if (!enableFieldSelection) return;
    const isSelected = selectedFields.some((f) => f.guid === field.guid);
    const updated = isSelected
      ? selectedFields.filter((f) => f.guid !== field.guid)
      : [...selectedFields, field];
    setSelectedFields(updated);
    setFieldsSelectInputError(updated.length === 0);
  };

  return (
    <Container style={{ textAlign: "left" }}>
      <Grid container spacing={2.5}>
        {/* Date, Seeding Method & Grass Type */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t("reseed.date.method.heading")}
          </Typography>
          <Box sx={cardSx}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <EnterDate
                  disabled={false}
                  date={reseedDate}
                  setDate={setReseedDate}
                  dateError={reseedDateError}
                  name={"reseedDate"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SeedingMethodDropdown
                  onChange={(value?: SEEDING_METHOD) => {
                    onSeedingMethodChange(value);
                  }}
                  seedingMethod={seedingMethod ?? ""}
                  seedingMethodInputError={seedingMethodError}
                  label={"reseedEvent.seedingMethod.text"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <GrassTypeDropdown
                  disabled={false}
                  onChange={(value?: GRASS_TYPE) => {
                    setNewGrassType(value);
                    if (value) {
                      setGrassTypeError(false);
                    }
                  }}
                  currentGrassType={newGrassType ?? ""}
                  grassTypeInputError={grassTypeError}
                  label={"reseedEvent.grassType.text"}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Field selection checkbox list */}
        <Grid size={12}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: fieldsSelectInputError ? "error.main" : undefined,
              fontWeight: 500,
            }}
          >
            {t("reseedEvents.selectFields.text")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              borderRadius: "8px",
              border: "1px solid",
              borderColor: fieldsSelectInputError
                ? "error.main"
                : "rgba(0, 0, 0, 0.12)",
              backgroundColor: "white",
              maxHeight: 220,
              overflowY: "auto",
              py: 0.5,
              px: 1,
              opacity: enableFieldSelection ? 1 : 0.5,
            }}
          >
            {[...allFields]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((field: Field) => {
                const isSelected = selectedFields.some(
                  (f) => f.guid === field.guid
                );
                return (
                  <FormControlLabel
                    key={field.guid}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() =>
                          handleFieldToggle({
                            guid: field.guid,
                            name: field.name,
                          })
                        }
                        size="small"
                        sx={{
                          color: "rgba(0,0,0,0.3)",
                          "&.Mui-checked": { color: "#7EC254" },
                        }}
                      />
                    }
                    label={field.name}
                    sx={{
                      mx: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.875rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                );
              })}
          </Box>
        </Grid>

        {/* Additional details (optional) */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t("common.additionalDetails.heading")}
          </Typography>
          <Box sx={cardSx}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  value={productName}
                  type="text"
                  id="productName"
                  fullWidth
                  onChange={(event) => {
                    setProductName(event.target.value);
                  }}
                  label={t("reseedEvent.productName.label")}
                  maxRows={1}
                  name={`productName`}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  value={seedingRate}
                  type="number"
                  id="seedingRate"
                  fullWidth
                  error={seedingRate !== undefined && seedingRate < 0}
                  onChange={(event) => {
                    if (event.target.value !== "") {
                      setSeedingRate(+event.target.value);
                    } else {
                      setSeedingRate(event.target.value as unknown as number);
                    }
                  }}
                  label={t("reseedEvent.seedingRate.label")}
                  maxRows={1}
                  name={`seedingRate`}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={notes}
                  id="reseedNotes"
                  fullWidth
                  onChange={(event) => {
                    setNotes(event.target.value);
                  }}
                  label={t("reseedEvent.notes.label")}
                  multiline
                  maxRows={12}
                />
              </Grid>
              {fileUploadSlot && <Grid size={12}>{fileUploadSlot}</Grid>}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
